import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSupportNotif } from '../../contexts/SupportNotifContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, Send, Mail, MessageCircle, CheckCircle, HelpCircle,
  Plus, ArrowLeft, Clock, CircleDot, CheckCircle2, XCircle,
  MessageSquare, ChevronRight, Inbox, Filter, RefreshCw, Lock,
  Paperclip, X, Image as ImageIcon
} from 'lucide-react';
import api from '../../lib/api';

// ==================== IMAGE UPLOAD HELPER ====================

function useImageUpload() {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]); // [{url, name, uploading}]

  const upload = async (fileList) => {
    const newFiles = [];
    setUploading(true);
    for (const file of Array.from(fileList)) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} — не изображение`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} — больше 10 МБ`);
        continue;
      }
      const preview = URL.createObjectURL(file);
      const placeholder = { url: preview, name: file.name, uploading: true };
      setFiles(prev => [...prev, placeholder]);
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await api.post('/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const uploaded = { url: res.data.file_path, name: file.name, uploading: false };
        setFiles(prev => prev.map(f => f.url === preview ? uploaded : f));
        newFiles.push(uploaded);
      } catch {
        toast.error(`Ошибка загрузки ${file.name}`);
        setFiles(prev => prev.filter(f => f.url !== preview));
      }
    }
    setUploading(false);
    return newFiles;
  };

  const remove = (url) => setFiles(prev => prev.filter(f => f.url !== url));
  const clear = () => setFiles([]);
  const urls = files.filter(f => !f.uploading).map(f => f.url);

  return { files, uploading, upload, remove, clear, urls };
}

function AttachmentPreviews({ files, onRemove, small }) {
  if (!files.length) return null;
  return (
    <div className={`flex flex-wrap gap-2 ${small ? 'mt-1' : 'mt-2'}`}>
      {files.map((f, i) => (
        <div key={f.url + i} className={`relative group rounded-lg overflow-hidden border border-border/50 ${small ? 'w-16 h-16' : 'w-20 h-20'}`}>
          <img src={f.url} alt={f.name} className="w-full h-full object-cover" />
          {f.uploading && (
            <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          )}
          {onRemove && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(f.url); }}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function AttachmentImages({ attachments, backendUrl }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  if (!attachments || !attachments.length) return null;
  return (
    <>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {attachments.map((url, i) => {
          const fullUrl = url.startsWith('http') ? url : `${backendUrl}${url}`;
          return (
            <button
              key={url + i}
              type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewUrl(fullUrl); }}
              className="block rounded-lg overflow-hidden border border-border/30 hover:border-primary/50 transition-colors cursor-zoom-in"
            >
              <img src={fullUrl} alt="" className="w-24 h-24 sm:w-32 sm:h-32 object-cover" loading="lazy" />
            </button>
          );
        })}
      </div>
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-2 sm:p-4 flex items-center justify-center bg-background/95 backdrop-blur-sm">
          {previewUrl && (
            <img
              src={previewUrl}
              alt=""
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function UploadButton({ onFiles, uploading, disabled }) {
  const inputRef = useRef(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { if (e.target.files.length) { onFiles(e.target.files); e.target.value = ''; } }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        title="Прикрепить фото"
      >
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
      </Button>
    </>
  );
}

// ==================== HELPERS ====================

const STATUS_MAP = {
  new: { label: 'Новый', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', icon: CircleDot },
  in_progress: { label: 'В работе', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: Clock },
  resolved: { label: 'Решён', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle2 },
  closed: { label: 'Закрыт', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400', icon: XCircle },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return 'только что';
    if (mins < 60) return `${mins} мин. назад`;
    if (hours < 24) return `${hours} ч. назад`;
    if (days === 1) return 'вчера';
    if (days < 7) return `${days} дн. назад`;

    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  } catch {
    return iso;
  }
}

function formatFullDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

// ==================== TICKET LIST ====================

function TicketList({ tickets, loading, onSelect, onRefresh, statusFilter, setStatusFilter }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 rounded-xl bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!tickets.length) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-muted/50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Inbox className="h-8 w-8 text-muted-foreground/50" />
        </div>
        <h3 className="text-base font-semibold mb-1">Нет обращений</h3>
        <p className="text-sm text-muted-foreground">
          {statusFilter ? 'Нет обращений с таким статусом' : 'Вы ещё не создавали обращений'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {tickets.map(ticket => {
        const st = STATUS_MAP[ticket.status] || STATUS_MAP.new;
        const StIcon = st.icon;
        const repliesCount = (ticket.replies || []).length;
        const replies = ticket.replies || [];
        const lastReply = replies.length > 0 ? replies[replies.length - 1] : null;
        const isUnread = ticket.read_by_user === false;
        const hasNewAdminReply = isUnread && lastReply && lastReply.author === 'admin';
        return (
          <Card
            key={ticket.id}
            style={{ marginBottom: '12px' }}
            className={`cursor-pointer transition-all hover:border-primary/40 hover:shadow-sm active:scale-[0.995] ${hasNewAdminReply ? 'border-l-[3px] border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/10' : ''}`}
            onClick={() => onSelect(ticket)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-xs font-mono text-muted-foreground">#{ticket.id}</span>
                    <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 h-5 ${st.color} border-0`}>
                      <StIcon className="h-3 w-3 mr-0.5" />
                      {st.label}
                    </Badge>
                    {hasNewAdminReply && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-0 animate-pulse">
                        <MessageSquare className="h-3 w-3 mr-0.5" />
                        Ответ поддержки
                      </Badge>
                    )}
                  </div>
                  <h4 className="font-semibold text-sm leading-tight line-clamp-1">{ticket.subject}</h4>
                  {hasNewAdminReply ? (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 line-clamp-1 font-medium">
                      {lastReply.author_name || 'Поддержка'}: {lastReply.message}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{ticket.message}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">{formatDate(ticket.updated_at || ticket.created_at)}</span>
                  <div className="flex items-center gap-2">
                    {repliesCount > 0 && (
                      <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                        <MessageSquare className="h-3 w-3" />
                        {repliesCount}
                      </span>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ==================== TICKET DETAIL ====================

function TicketDetail({ ticket, onClose, onUpdate }) {
  const { user } = useAuth();
  const { refresh: refreshSupportNotif } = useSupportNotif();
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [localTicket, setLocalTicket] = useState(ticket);
  const messagesEndRef = useRef(null);
  const imgUpload = useImageUpload();
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Refresh ticket data — GET marks it as read on backend
  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const res = await api.get(`/support/tickets/${ticket.id}`);
        setLocalTicket(res.data);
        refreshSupportNotif();
      } catch {}
    };
    fetchTicket();
  }, [ticket.id, refreshSupportNotif]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localTicket?.replies]);

  const handleReply = async () => {
    if (!replyText.trim() && !imgUpload.urls.length) return;
    setSending(true);
    try {
      const res = await api.post(`/support/tickets/${localTicket.id}/reply`, {
        message: replyText.trim(),
        attachments: imgUpload.urls
      });
      setLocalTicket(prev => ({
        ...prev,
        replies: [...(prev.replies || []), res.data.reply],
        updated_at: new Date().toISOString()
      }));
      setReplyText('');
      imgUpload.clear();
      toast.success('Ответ отправлен');
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await api.put(`/support/tickets/${localTicket.id}/close`);
      setLocalTicket(prev => ({ ...prev, status: 'closed' }));
      toast.success('Тикет закрыт');
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ошибка');
    } finally {
      setClosing(false);
    }
  };

  const st = STATUS_MAP[localTicket.status] || STATUS_MAP.new;
  const StIcon = st.icon;
  const isClosed = localTicket.status === 'closed';
  const replies = localTicket.replies || [];

  return (
    <Sheet open onOpenChange={() => onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-4 sm:px-6 pt-5 pb-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-mono text-muted-foreground">#{localTicket.id}</span>
                <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 h-5 ${st.color} border-0`}>
                  <StIcon className="h-3 w-3 mr-0.5" />
                  {st.label}
                </Badge>
              </div>
              <SheetTitle className="text-base leading-tight line-clamp-1 text-left">{localTicket.subject}</SheetTitle>
            </div>
          </div>
        </SheetHeader>

        {/* Messages */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-4 sm:px-6 py-4 space-y-4">
            {/* Original message */}
            <div className="flex justify-end">
              <div className="max-w-[85%]">
                <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-4 py-3">
                  <p className="text-sm whitespace-pre-wrap break-words">{localTicket.message}</p>
                  <AttachmentImages attachments={localTicket.attachments} backendUrl={backendUrl} />
                </div>
                <p className="text-[11px] text-muted-foreground text-right mt-1">
                  {formatFullDate(localTicket.created_at)}
                </p>
              </div>
            </div>

            {/* Replies */}
            {replies.map((reply) => {
              const isUser = reply.author === 'user';
              return (
                <div key={reply.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[85%]">
                    {!isUser && (
                      <p className="text-[11px] text-muted-foreground mb-1 ml-1 flex items-center gap-1">
                        <span className="w-4 h-4 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                          <HelpCircle className="h-2.5 w-2.5 text-emerald-600 dark:text-emerald-400" />
                        </span>
                        {reply.author_name || 'Поддержка'}
                      </p>
                    )}
                    <div className={
                      isUser
                        ? 'bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-4 py-3'
                        : 'bg-muted rounded-2xl rounded-tl-md px-4 py-3'
                    }>
                      <p className="text-sm whitespace-pre-wrap break-words">{reply.message}</p>
                      <AttachmentImages attachments={reply.attachments} backendUrl={backendUrl} />
                    </div>
                    <p className={`text-[11px] text-muted-foreground mt-1 ${isUser ? 'text-right' : 'text-left ml-1'}`}>
                      {formatFullDate(reply.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}

            {/* Closed banner */}
            {isClosed && (
              <div className="flex justify-center">
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-full px-4 py-2">
                  <Lock className="h-3 w-3" />
                  Тикет закрыт
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Reply input */}
        {!isClosed ? (
          <div className="border-t px-4 sm:px-6 py-3 shrink-0 space-y-2">
            <AttachmentPreviews files={imgUpload.files} onRemove={imgUpload.remove} small />
            <div className="flex gap-2 items-end">
              <Textarea
                placeholder="Напишите ответ..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={2}
                className="resize-none text-sm flex-1"
                maxLength={5000}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleReply();
                  }
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground h-8" onClick={handleClose} disabled={closing}>
                  {closing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                  Закрыть тикет
                </Button>
              </div>
              <div className="flex items-center gap-1">
                <UploadButton onFiles={imgUpload.upload} uploading={imgUpload.uploading} />
                <span className="text-[10px] text-muted-foreground hidden sm:inline">Ctrl+Enter</span>
                <Button size="sm" onClick={handleReply} disabled={sending || (!replyText.trim() && !imgUpload.urls.length)} className="h-8">
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                  Отправить
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="border-t px-4 sm:px-6 py-3 shrink-0">
            <p className="text-xs text-muted-foreground text-center">
              Этот тикет закрыт. Если проблема не решена — <button className="text-primary hover:underline" onClick={() => { onClose(); }}>создайте новое обращение</button>.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ==================== NEW TICKET FORM ====================

function NewTicketForm({ onCreated, onCancel }) {
  const { user } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const imgUpload = useImageUpload();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Заполните тему и текст сообщения');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/support/message', {
        subject: subject.trim(),
        message: message.trim(),
        attachments: imgUpload.urls
      });
      toast.success(res.data?.message || 'Сообщение отправлено!');
      onCreated?.(res.data?.ticket);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Ошибка отправки. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <HelpCircle className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Новое обращение</CardTitle>
            <CardDescription>Мы ответим на {user?.email || 'вашу почту'}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Тема</Label>
            <Input
              id="subject"
              placeholder="Кратко опишите вопрос"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              maxLength={200}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Сообщение</Label>
            <Textarea
              id="message"
              placeholder="Подробно опишите вашу проблему или вопрос..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={5}
              className="resize-none"
              maxLength={5000}
            />
            <p className="text-xs text-muted-foreground text-right">{message.length} / 5000</p>
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Вложения</Label>
              <UploadButton onFiles={imgUpload.upload} uploading={imgUpload.uploading} />
            </div>
            <AttachmentPreviews files={imgUpload.files} onRemove={imgUpload.remove} />
            {!imgUpload.files.length && (
              <p className="text-xs text-muted-foreground">Можно прикрепить фото (до 10 МБ)</p>
            )}
          </div>

          <div className="flex gap-2">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Назад
              </Button>
            )}
            <Button type="submit" className="flex-1 sm:flex-initial" disabled={loading || imgUpload.uploading}>
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Отправить
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ==================== MAIN PAGE ====================

export default function SupportPage() {
  const { user } = useAuth();
  const { refresh: refreshSupportNotif } = useSupportNotif();
  const [view, setView] = useState('list'); // 'list' | 'new' | 'detail'
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [tgUsername, setTgUsername] = useState('xukep');

  useEffect(() => {
    api.get('/support/contact-info').then(res => {
      setTgUsername(res.data?.telegram_username || 'xukep');
    }).catch(() => {});
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/support/tickets', { params });
      setTickets(res.data?.tickets || []);
    } catch (err) {
      console.error('Failed to load tickets', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleTicketCreated = (ticket) => {
    if (ticket) {
      setTickets(prev => [ticket, ...prev]);
    } else {
      fetchTickets();
    }
    setView('list');
    refreshSupportNotif();
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setView('detail');
  };

  const handleCloseDetail = () => {
    setSelectedTicket(null);
    setView('list');
    fetchTickets();
    refreshSupportNotif();
  };

  const openCount = tickets.filter(t => t.status === 'new' || t.status === 'in_progress').length;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Поддержка</h1>
          <p className="text-sm text-muted-foreground">
            {tickets.length > 0
              ? `${tickets.length} обращений${openCount > 0 ? ` · ${openCount} открытых` : ''}`
              : 'Свяжитесь с нами — мы поможем'
            }
          </p>
        </div>
        {view === 'list' && (
          <Button onClick={() => setView('new')} size="sm" className="shrink-0">
            <Plus className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Новое обращение</span>
            <span className="sm:hidden">Создать</span>
          </Button>
        )}
      </div>

      {/* Telegram fallback */}
      <div>
        <a
          href={`https://t.me/${tgUsername}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-orange-50/80 dark:bg-orange-950/30 border border-orange-200/60 dark:border-orange-800/40 hover:bg-orange-100/80 dark:hover:bg-orange-950/50 transition-colors group cursor-pointer"
        >
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-orange-500 shrink-0" />
            <p className="text-xs text-orange-700/80 dark:text-orange-300/80">
              Резервный канал связи — <span className="text-primary font-medium group-hover:underline">@{tgUsername} в Telegram</span>
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-orange-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </a>
      </div>

      {/* Main content */}
      {view === 'new' ? (
        <div className="max-w-2xl">
          <NewTicketForm
            onCreated={handleTicketCreated}
            onCancel={() => setView('list')}
          />
        </div>
      ) : (
        <>
          {/* Filters bar */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 flex-1">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-8 w-[140px] text-xs">
                  <SelectValue placeholder="Все статусы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="new">Новые</SelectItem>
                  <SelectItem value="in_progress">В работе</SelectItem>
                  <SelectItem value="resolved">Решённые</SelectItem>
                  <SelectItem value="closed">Закрытые</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchTickets} title="Обновить">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Ticket list */}
          <TicketList
            tickets={tickets}
            loading={loading}
            onSelect={handleSelectTicket}
            onRefresh={fetchTickets}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
        </>
      )}

      {/* Ticket detail sheet */}
      {view === 'detail' && selectedTicket && (
        <TicketDetail
          ticket={selectedTicket}
          onClose={handleCloseDetail}
          onUpdate={fetchTickets}
        />
      )}
    </div>
  );
}
